import { useState, useEffect } from "react";
import BaseModal from "../../../BaseComponents/BaseModal/BaseModal";
import styles from "./AddCabinClassModal.module.css";

const emptyForm = {
  cabinClassCode: "",
  cabinClassName: "",
  rank: "",
  status: "active"
};

const AddCabinClassModal = ({ open, onClose, onSave, cabinClass, apiError = null }) => {
  const [form, setForm] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [saving, setSaving] = useState(false);

  // Populate form when editing
  useEffect(() => {
    if (cabinClass) {
      setForm({
        cabinClassCode: cabinClass.cabinClassCode || "",
        cabinClassName: cabinClass.cabinClassName || "",
        rank: cabinClass.rank?.toString() || "",
        status: cabinClass.status || "active"
      });
    } else {
      setForm(emptyForm);
    }
    setErrors({});
    setTouched({});
  }, [cabinClass, open]);

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    // Clear error when user starts typing
    if (errors[key]) {
      setErrors(prev => ({ ...prev, [key]: "" }));
    }
  };

  const handleBlur = (key) => {
    setTouched(prev => ({ ...prev, [key]: true }));
    validateField(key, form[key]);
  };

  const validateField = (key, value) => {
    let error = "";

    switch (key) {
      case "cabinClassCode":
        if (!value || !value.trim()) {
          error = "Cabin class code is required";
        } else if (value.trim().length < 1) {
          error = "Code must be at least 1 character";
        } else if (!/^[A-Z_]+$/.test(value)) {
          error = "Code must contain only uppercase letters and underscores";
        }
        break;
      case "cabinClassName":
        if (!value || !value.trim()) {
          error = "Cabin class name is required";
        } else if (value.trim().length < 2) {
          error = "Name must be at least 2 characters";
        }
        break;
      case "rank":
        if (!value) {
          error = "Rank is required";
        } else if (isNaN(value) || parseInt(value) < 1) {
          error = "Rank must be a positive number";
        }
        break;
      default:
        break;
    }

    setErrors(prev => ({ ...prev, [key]: error }));
    return error;
  };

  const validateAllFields = () => {
    const newErrors = {};
    let isValid = true;

    if (!form.cabinClassCode.trim()) {
      newErrors.cabinClassCode = "Cabin class code is required";
      isValid = false;
    } else if (form.cabinClassCode.trim().length < 1) {
      newErrors.cabinClassCode = "Code must be at least 1 character";
      isValid = false;
    } else if (!/^[A-Z_]+$/.test(form.cabinClassCode)) {
      newErrors.cabinClassCode = "Code must contain only uppercase letters and underscores";
      isValid = false;
    }

    if (!form.cabinClassName.trim()) {
      newErrors.cabinClassName = "Cabin class name is required";
      isValid = false;
    } else if (form.cabinClassName.trim().length < 2) {
      newErrors.cabinClassName = "Name must be at least 2 characters";
      isValid = false;
    }

    if (!form.rank) {
      newErrors.rank = "Rank is required";
      isValid = false;
    } else if (isNaN(form.rank) || parseInt(form.rank) < 1) {
      newErrors.rank = "Rank must be a positive number";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleReset = () => {
    setForm(emptyForm);
    setErrors({});
    setTouched({});
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  const handleSubmit = async () => {
    const isValid = validateAllFields();

    if (!isValid) {
      return;
    }

    setSaving(true);
    const payload = {
      cabinClassCode: form.cabinClassCode.trim().toUpperCase(),
      cabinClassName: form.cabinClassName.trim(),
      rank: parseInt(form.rank),
      status: form.status
    };

    try {
      const errorMsg = await onSave(payload);
      if (errorMsg) {
        // Error shown via apiError prop
      } else {
        handleClose();
      }
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0 && Object.values(errors).some(e => e !== "");

  return (
    <BaseModal
      open={open}
      onClose={handleClose}
      title={cabinClass ? "Edit Cabin Class" : "Add New Cabin Class"}
      width="680px"
      footer={
        <>
          <button className={styles.closeBtn} onClick={handleClose}>
            ✕ Close
          </button>
          <button className={styles.saveBtn} onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </>
      }
    >
      {/* Icon Header */}
      <div className={styles.iconHeader}>
        <div className={styles.icon}>💺</div>
        <div className={styles.headerText}>
          <h3>{cabinClass ? "Edit Cabin Class Details" : "Add New Cabin Class"}</h3>
          <p>
            {cabinClass 
              ? "Update cabin class master data used across flight bookings and fare configurations"
              : "Create a new cabin class entry for flight bookings and fare assignments"}
          </p>
        </div>
      </div>

      {/* API Error Banner */}
      {apiError && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Save failed:</strong> {apiError}
          </div>
        </div>
      )}

      {/* Validation Error Banner */}
      {hasErrors && (
        <div className={styles.errorBanner}>
          <div className={styles.errorIcon}>⚠️</div>
          <div className={styles.errorContent}>
            <strong>Please fix the following errors:</strong>
            <ul>
              {Object.entries(errors).map(([key, error]) => 
                error && <li key={key}>{error}</li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* Basic Information Section */}
      <div className={styles.section}>
        <h4 className={styles.sectionTitle}>✏️ BASIC INFORMATION</h4>
        <div className={styles.formGrid}>
          <div className={styles.field}>
            <label>CABIN CLASS CODE <span className={styles.required}>*</span></label>
            <input
              value={form.cabinClassCode}
              onChange={e => handleChange("cabinClassCode", e.target.value.toUpperCase())}
              onBlur={() => handleBlur("cabinClassCode")}
              placeholder="e.g., ECONOMY, BUSINESS, FIRST"
              className={`${styles.codeInput} ${errors.cabinClassCode ? styles.inputError : ""}`}
            />
            {errors.cabinClassCode && (
              <span className={styles.errorText}>{errors.cabinClassCode}</span>
            )}
            <span className={styles.helpText}>
              Unique identifier (uppercase letters and underscores only)
            </span>
          </div>

          <div className={styles.field}>
            <label>RANK (SORTING ORDER) <span className={styles.required}>*</span></label>
            <input
              type="number"
              value={form.rank}
              onChange={e => handleChange("rank", e.target.value)}
              onBlur={() => handleBlur("rank")}
              placeholder="1"
              min="1"
              className={errors.rank ? styles.inputError : ""}
            />
            {errors.rank && (
              <span className={styles.errorText}>{errors.rank}</span>
            )}
            <span className={styles.helpText}>
              Lower numbers appear first (1 = highest priority)
            </span>
          </div>

          <div className={styles.fieldWide}>
            <label>CABIN CLASS NAME <span className={styles.required}>*</span></label>
            <input
              value={form.cabinClassName}
              onChange={e => handleChange("cabinClassName", e.target.value)}
              onBlur={() => handleBlur("cabinClassName")}
              placeholder="e.g., Economy Class, Business Class, First Class"
              className={errors.cabinClassName ? styles.inputError : ""}
            />
            {errors.cabinClassName && (
              <span className={styles.errorText}>{errors.cabinClassName}</span>
            )}
            <span className={styles.helpText}>
              Display name shown to customers in search and booking
            </span>
          </div>

          <div className={styles.field}>
            <label>STATUS</label>
            <div className={styles.statusToggle}>
              <input
                type="checkbox"
                id="cabinClassStatus"
                checked={form.status === "active"}
                onChange={e => handleChange("status", e.target.checked ? "active" : "inactive")}
                className={styles.toggleInput}
              />
              <label htmlFor="cabinClassStatus" className={styles.toggleLabel}>
                <span className={styles.toggleSwitch}></span>
                <span className={styles.toggleText}>
                  {form.status === "active" ? "Active" : "Inactive"}
                </span>
              </label>
            </div>
            <span className={styles.helpText}>
              Only active cabin classes appear in flight search and bookings
            </span>
          </div>
        </div>
      </div>

      {/* Rank Examples */}
      <div className={styles.examplesSection}>
        <h4 className={styles.sectionTitle}>📊 RANK EXAMPLES</h4>
        <div className={styles.examplesGrid}>
          <div className={styles.exampleCard}>
            <div className={styles.exampleRank} style={{ background: '#fef3c715', color: '#f59e0b' }}>
              1
            </div>
            <div className={styles.exampleText}>
              <strong>First Class</strong>
              <span>Highest priority</span>
            </div>
          </div>
          <div className={styles.exampleCard}>
            <div className={styles.exampleRank} style={{ background: '#e0e7ff', color: '#6366f1' }}>
              2
            </div>
            <div className={styles.exampleText}>
              <strong>Business Class</strong>
              <span>Second priority</span>
            </div>
          </div>
          <div className={styles.exampleCard}>
            <div className={styles.exampleRank} style={{ background: '#dbeafe', color: '#3b82f6' }}>
              3
            </div>
            <div className={styles.exampleText}>
              <strong>Premium Economy</strong>
              <span>Third priority</span>
            </div>
          </div>
          <div className={styles.exampleCard}>
            <div className={styles.exampleRank} style={{ background: '#f3f4f6', color: '#6b7280' }}>
              4
            </div>
            <div className={styles.exampleText}>
              <strong>Economy Class</strong>
              <span>Standard priority</span>
            </div>
          </div>
        </div>
      </div>

      {/* Business Rules Notice */}
      <div className={styles.businessNotice}>
        <div className={styles.noticeIcon}>📋</div>
        <div className={styles.noticeContent}>
          <strong>Business Rules</strong>
          <ul>
            <li>Cabin class codes must be unique across all entries</li>
            <li>Rank determines display order in search results (lower = higher priority)</li>
            <li>Cabin classes are used by fare classes for pricing and availability</li>
            <li>Inactive cabin classes won't appear in flight search or booking flows</li>
          </ul>
        </div>
      </div>
    </BaseModal>
  );
};

export default AddCabinClassModal;